import type { ModelInfo } from '@/api-models/models';

/**
 * Client-side rules for chat image attachments.
 *
 * Mirrors `backend/crates/coding-agent/src/images.rs` — the backend rejects the same payloads on
 * the REST and WS prompt paths, this module is the UX guard that stops a user getting there.
 * The numbers come from Fireworks: < 10 MB of base64 per request and at most 30 images, so the
 * cap sits at 8 MiB to leave room for the rest of the JSON body.
 */

export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
export const MAX_IMAGES = 30;

export const ALLOWED_IMAGE_MIME = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'image/x-portable-pixmap',
] as const;

/** `accept` for the file picker: extensions Fireworks lists, plus the MIME types for paste/drop. */
export const IMAGE_ACCEPT = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.ppm', ...ALLOWED_IMAGE_MIME].join(',');

/** Longest edge kept when re-encoding. Beyond this the extra pixels only cost tokens. */
const MAX_EDGE = 1568;

/** Only these re-encode through a canvas; the rest either animate or browsers can't decode them. */
const REENCODABLE = new Set(['image/png', 'image/jpeg']);

/**
 * Whether the served-model catalogue says this model can read images.
 *
 * Looked up rather than inferred from the name: this used to match `kimi` as a substring, which
 * was true of every vision model at the time and silently wrong the moment one arrived that
 * wasn't called Kimi. `models` comes from `useModels()`; while it is still loading the answer is
 * `false`, and an unknown model stays `false` too — the safe direction, since the cost of a
 * wrong `true` is a hard provider error mid-turn.
 *
 * Mirrors `ResolvedModel::supports_vision` on the backend, which holds the authoritative gate in
 * `llm_client::build_request_body`.
 */
export function supportsImages(models: ModelInfo[], model: string | null | undefined): boolean {
  if (!model) return false;
  return models.some((m) => m.id === model && m.supports_vision);
}

/** Base64 payload length of a data URL, i.e. what counts against the Fireworks budget. */
export function imageBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  return comma === -1 ? 0 : dataUrl.length - comma - 1;
}

export function totalImageBytes(images: string[]): number {
  return images.reduce((sum, image) => sum + imageBytes(image), 0);
}

/** `null` when the set is sendable, otherwise a message to show the user. */
export function validateImages(images: string[]): string | null {
  if (images.length > MAX_IMAGES) {
    return `Too many images: ${images.length} (max ${MAX_IMAGES}).`;
  }
  const total = totalImageBytes(images);
  if (total > MAX_IMAGE_BYTES) {
    return `Images are too large: ${formatBytes(total)} (max ${formatBytes(MAX_IMAGE_BYTES)}).`;
  }
  for (const image of images) {
    const match = /^data:([^;,]+);base64,(.+)$/s.exec(image);
    if (!match) return 'Only base64 data URL images are supported.';
    if (!(ALLOWED_IMAGE_MIME as readonly string[]).includes(match[1]!)) {
      return `Unsupported image type ${match[1]}. Use PNG, JPEG, GIF, BMP, TIFF or PPM.`;
    }
  }
  return null;
}

export function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
}

export function isSupportedImageFile(file: File): boolean {
  return (ALLOWED_IMAGE_MIME as readonly string[]).includes(file.type);
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Could not read the image.'));
    reader.readAsDataURL(file);
  });
}

/**
 * Read an image as a base64 data URL, downscaling oversized PNG/JPEG through a canvas first.
 *
 * Phone screenshots and retina captures blow the per-request budget on their own, so shrinking
 * here is what makes attaching more than one or two of them possible at all.
 */
export async function fileToDataUrl(file: File): Promise<string> {
  const original = await readAsDataUrl(file);
  if (!REENCODABLE.has(file.type)) return original;

  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return original;

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  // A small PNG of flat UI colours can grow when forced through JPEG — keep whichever is smaller.
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return original;
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const encoded = canvas.toDataURL('image/jpeg', 0.85);
  return imageBytes(encoded) < imageBytes(original) ? encoded : original;
}

export interface AddImagesResult {
  images: string[];
  /** Why some files didn't make it, ready to toast. */
  rejected: string[];
}

/** Append newly picked/pasted/dropped images to the existing set, enforcing the caps. */
export function addImages(existing: string[], incoming: string[]): AddImagesResult {
  const images = [...existing];
  const rejected: string[] = [];
  let total = totalImageBytes(existing);

  for (const image of incoming) {
    if (images.length >= MAX_IMAGES) {
      rejected.push(`Only ${MAX_IMAGES} images can be attached to a message.`);
      break;
    }
    const bytes = imageBytes(image);
    if (total + bytes > MAX_IMAGE_BYTES) {
      rejected.push(`Attachments would exceed ${formatBytes(MAX_IMAGE_BYTES)} — some images were skipped.`);
      break;
    }
    images.push(image);
    total += bytes;
  }

  return { images, rejected };
}
